import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { loginAndGetToken, loadAuthUser, saveAuthUser, verifyJWTToken, refreshJWTToken, logout, handleGoogleRedirectResult } from '../services/auth';
import { getUserStorageKey, migrateLegacyData } from '../utils/storage';
import { getUserProfile, updateUserProfile } from '../services/userService';
const appleWebConfigured = !!(import.meta as any).env?.VITE_APPLE_WEB_CLIENT_ID;

function isWeChatBrowser(): boolean {
  return /MicroMessenger/i.test(navigator.userAgent);
}


const Splash: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useAppSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  // web 端页面加载时先完成所有初始化检查，避免已登录用户看到登录 UI 闪现
  const [isInitializing, setIsInitializing] = useState(Capacitor.getPlatform() === 'web');
  const showAppleLogin = Capacitor.getPlatform() !== 'web' || appleWebConfigured;

  const location = useLocation();
  const forceLogin = location.state?.forceLogin;

  // Capgo Web Google 登录：弹窗会打开同源 /splash#access_token=…
  // 若此处按「访客已完成引导」立刻 navigate('/whisper')，会丢掉 hash，主窗口收不到 OAuth 结果。
  const OAUTH_PENDING_KEY = 'social_login_oauth_pending';

  // Native 平台：检查用户是否已登录
  useEffect(() => {
    if (Capacitor.getPlatform() === 'web') return;

    const user = loadAuthUser();
    setIsLoggedIn(!!user);

    if (user?.id) {
      const completedKey = getUserStorageKey('onboarding-completed', user.id);
      (async () => {
        try {
          const forceOnboarding = localStorage.getItem('t2s_debug_force_onboarding') === '1';
          const isValid = await verifyJWTToken();
          if (!isValid) {
            const refreshed = await refreshJWTToken();
            if (!refreshed) throw new Error('Refresh failed');
          }
          const profile = await getUserProfile();
          const hasNickname = !!profile.nickname;
          if (hasNickname && !forceOnboarding) {
            localStorage.setItem(completedKey, '1');
            saveAuthUser({ ...user, nickname: profile.nickname || undefined, is_admin: profile.is_admin });
            navigate('/whisper', { replace: true });
          } else {
            if (forceOnboarding) localStorage.removeItem('t2s_debug_force_onboarding');
            navigate('/onboarding', { replace: true });
          }
        } catch (e) {
          console.warn('[Splash] Auth validation failed, logging out:', e);
          await logout();
          navigate('/', { replace: true });
        }
      })();
    } else {
      const guestCompleted = localStorage.getItem('onboarding-completed');
      if (guestCompleted) navigate('/whisper', { replace: true });
    }
  }, [forceLogin, navigate]);

  // Web 平台：顺序执行 redirect check → auth check，避免并发导致登录 UI 闪现
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'web') return;

    if (forceLogin) {
      setIsInitializing(false);
      return;
    }

    if (
      localStorage.getItem(OAUTH_PENDING_KEY) ||
      /\baccess_token=|\bid_token=|\bcode=/.test(window.location.hash || '')
    ) {
      setIsInitializing(false);
      return;
    }

    (async () => {
      // Step 1: 先检查是否从 Google redirect 回来
      try {
        const result = await handleGoogleRedirectResult();
        if (result) {
          setIsLoading(true);
          setIsLoggedIn(true);
          await handlePostLogin(result.user);
          return; // navigate 后组件 unmount，后续不再执行
        }
      } catch (e: any) {
        console.error('[Splash] Google redirect result error:', e);
      } finally {
        setIsLoading(false);
      }

      // Step 2: 无 redirect → 检查已存储的登录状态
      const user = loadAuthUser();
      setIsLoggedIn(!!user);

      if (user?.id) {
        const completedKey = getUserStorageKey('onboarding-completed', user.id);
        try {
          const forceOnboarding = localStorage.getItem('t2s_debug_force_onboarding') === '1';
          const isValid = await verifyJWTToken();
          if (!isValid) {
            console.log('[Splash] Token invalid, attempting refresh...');
            const refreshed = await refreshJWTToken();
            if (!refreshed) throw new Error('Refresh failed');
          }
          const profile = await getUserProfile();
          const hasNickname = !!profile.nickname;
          if (hasNickname && !forceOnboarding) {
            console.log('[Splash] Valid nickname found, skipping onboarding.');
            localStorage.setItem(completedKey, '1');
            saveAuthUser({ ...user, nickname: profile.nickname || undefined, is_admin: profile.is_admin });
            navigate('/whisper', { replace: true });
          } else {
            console.log('[Splash] Nickname missing or forced, redirecting to Onboarding.');
            if (forceOnboarding) localStorage.removeItem('t2s_debug_force_onboarding');
            navigate('/onboarding', { replace: true });
          }
        } catch (e) {
          console.warn('[Splash] Auth validation failed, logging out:', e);
          await logout();
          setIsInitializing(false);
          navigate('/', { replace: true });
        }
      } else {
        const guestCompleted = localStorage.getItem('onboarding-completed');
        if (guestCompleted) {
          navigate('/whisper', { replace: true });
        } else {
          setIsInitializing(false); // 未登录访客 → 显示登录 UI
        }
      }
    })();
  }, [forceLogin, navigate]);

  const syncPendingOnboardingData = async (userId: string) => {
      // Check for guest data (null userId key) AND user specific data
      const keysToCheck = [
          'onboarding-data', 
          getUserStorageKey('onboarding-data', userId)
      ];
      
      for (const key of keysToCheck) {
          const dataStr = localStorage.getItem(key);
          if (dataStr) {
              try {
                  const data = JSON.parse(dataStr);
                  if (data.name) {
                      console.log(`[Splash] Found pending onboarding data in ${key}, syncing to backend...`, data);
                      await updateUserProfile({ nickname: data.name });
                      
                      // Update local user
                      const u = loadAuthUser();
                      if (u) saveAuthUser({ ...u, nickname: data.name });
                      
                      // Clear data
                      localStorage.removeItem(key);
                      return true; // We did a sync
                  }
              } catch (e) {
                  console.error(`[Splash] Failed to sync data from ${key}:`, e);
              }
          }
      }
      return false;
  };

  const handlePostLogin = async (user: any) => {
      // 1. Migrate legacy keys
      const completedKey = getUserStorageKey('onboarding-completed', user.id);
      migrateLegacyData('onboarding-completed', user.id);
      
      // 2. Mandatory: Fetch latest profile to sync nickname and is_admin
      try {
          const profile = await getUserProfile();
          if (profile) {
              console.log('[Splash] Post-login sync: Found profile, updating local user.', profile);
              user = { ...user, nickname: profile.nickname, is_admin: profile.is_admin };
              saveAuthUser(user);
          }
      } catch (e) {
          console.error('[Splash] Post-login profile sync failed:', e);
      }

      // 2.5 Sync device timezone to backend (for conversation auto check-in)
      try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) {
              await updateUserProfile({ timezone: tz });
              console.log('[Splash] Synced timezone to backend:', tz);
          }
      } catch (e) {
          console.error('[Splash] Timezone sync failed:', e);
      }

      // 3. Check & Sync Guest Data
      const synced = await syncPendingOnboardingData(user.id);
      
      // 4. Navigation Logic
      const isForced = localStorage.getItem('t2s_debug_force_onboarding') === '1';

      if (synced) {
          // If we synced, we definitely completed onboarding
          localStorage.setItem(completedKey, '1');
          localStorage.removeItem('t2s_debug_force_onboarding');
          navigate('/whisper', { replace: true });
      } else {
           // Mandatory Nickname Check (Ignore local 'completed' flag if nickname is missing)
           // This forces any old user without a nickname into onboarding.
           if (user.nickname && !isForced) {
               localStorage.setItem(completedKey, '1');
               navigate('/whisper', { replace: true });
           } else {
               if (isForced) {
                   console.log('[Splash] Post-login: Force Onboarding active, skipping recovery.');
                   localStorage.removeItem('t2s_debug_force_onboarding');
               }
               navigate('/onboarding', { replace: true, state: { prefillName: user.full_name || undefined } });
           }
      }
  };


  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setLoginError(null);
      const { user } = await loginAndGetToken('google');
      console.log('Google login + JWT result:', user);
      setIsLoggedIn(true);
      await handlePostLogin(user);
    } catch (e: any) {
      console.error('Google login error:', e);
      setLoginError(t('auth.googleLoginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      setIsLoading(true);
      setLoginError(null);
      const { user } = await loginAndGetToken('apple');
      console.log('Apple login + JWT result:', user);
      setIsLoggedIn(true);
      await handlePostLogin(user);
    } catch (e: any) {
      console.error('Apple login error:', e);
      // #region agent log H-B H-C: final error in Splash
      try{const _d={sessionId:'9ab244',loc:'Splash.tsx:handleAppleLogin:catch',msg:'Apple login error',code:e?.code,codeType:typeof e?.code,isCanceled:e?.isCanceled,isSysErr:e?.isAppleSystemError,errMsg:e?.message,errName:e?.name,ts:Date.now(),hyp:'H-B,H-C'};try{const _p=JSON.parse(localStorage.getItem('_dbg9ab244')||'[]');_p.push(_d);localStorage.setItem('_dbg9ab244',JSON.stringify(_p));}catch(_){}fetch('http://127.0.0.1:7791/ingest/26a0facd-5b8c-473d-9da8-40faa6cbd7e3',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'9ab244'},body:JSON.stringify(_d)}).catch(()=>{});}catch(_){}
      // #endregion
      if (e?.code === 'APPLE_SIGNIN_CANCELED' || e?.isCanceled) {
        return; // silent: user tapped Cancel in the Apple sheet
      }
      setLoginError(t('auth.appleLoginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    // 如果已登录，直接跳转到主页面
    if (isLoggedIn) {
      navigate('/whisper', { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      // Ensure no stale auth data
      localStorage.removeItem('auth-user');
      localStorage.removeItem('jwt_token');
      setIsLoggedIn(false);

      // Guest flow: no Onboarding needed.
      // Default nickname = "Guest", default persona = "listener" (Whisper page defaults).
      // Mark guest as onboarded so Profile and auto-restart work correctly.
      localStorage.setItem('onboarding-completed', '1');

      navigate('/whisper', { replace: true });
    } catch (e: any) {
      console.error('Guest navigation error:', e);
      navigate('/whisper', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/whisper', { replace: true });
  };

  // web 端初始化检查中（避免已登录用户短暂看到登录 UI）
  if (isInitializing) {
    return (
      <div className="h-screen w-full flex items-center justify-center" style={{
        background: 'linear-gradient(180deg, #0B1E54 0%, #0E2F7A 40%, #0E49A6 100%)'
      }}>
        <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative h-screen w-full overflow-hidden flex flex-col" style={{
      background: 'linear-gradient(180deg, #0B1E54 0%, #0E2F7A 40%, #0E49A6 100%)'
    }}>
      {/* Star and Title */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-0 overflow-y-auto">
        {/* Star */}
        <img
          src="/images/idle_star.png"
          alt="star"
          className="pointer-events-none select-none mb-8"
          style={{
            width: 'clamp(8rem, 20vw, 12rem)',
            height: 'clamp(8rem, 20vw, 12rem)',
            animation: 'float 3s ease-in-out infinite'
          }}
        />
        <style>{`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
              filter: brightness(0.9) drop-shadow(0 0 10px rgba(255, 217, 0, 0.18));
            }
            50% {
              transform: translateY(-12px);
              filter: brightness(1.1) drop-shadow(0 0 30px rgba(255, 217, 0, 0.7));
            }
          }
        `}</style>
        {/* Title - Two lines with different font sizes */}
        <h1
          className="text-white font-light italic mb-2 text-center"
          style={{ fontSize: 'clamp(1.5rem, 8vw, 3rem)' }}
        >
          {t('splash.title')}
        </h1>
        <p
          className="text-white/90 font-normal text-center"
          style={{ fontSize: 'clamp(0.875rem, 4vw, 1.125rem)' }}
        >
          {t('splash.subtitle')}
        </p>
      </div>

      {/* Login Buttons or Continue Button */}
      <div className="px-6 pb-6 pt-2 space-y-3 flex-shrink-0">
        {/* Inline login error — replaces alert() to avoid triggering Capacitor's native "Load failed" dialog */}
        {loginError && (
          <div className="rounded-xl px-4 py-3 text-center" style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
          }}>
            <p className="text-red-200 text-sm">{loginError}</p>
          </div>
        )}
        {/* 微信浏览器提示 */}
        {Capacitor.getPlatform() === 'web' && isWeChatBrowser() && (
          <div className="bg-yellow-500/20 border border-yellow-400/40 rounded-xl p-4 text-center">
            <p className="text-yellow-200 text-sm font-medium mb-1">请用浏览器打开</p>
            <p className="text-yellow-200/70 text-xs">微信内不支持 Google 登录，请点击右上角 ··· → 选择在浏览器中打开</p>
          </div>
        )}
        {!isLoggedIn ? (
          <>
            {/* Google Login Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading || (Capacitor.getPlatform() === 'web' && isWeChatBrowser())}
              className="w-10/12 mx-auto rounded-full px-6 py-4 text-white transition-all flex items-center justify-center gap-3 overflow-hidden relative"
              style={{
                background: 'linear-gradient(-3deg,rgba(14, 72, 166, 0.18) 0%, rgba(11, 30, 84, 0.38) 100%)',
                backdropFilter: 'blur(90px) saturate(100%)',
                WebkitBackdropFilter: 'blur(90px) saturate(100%)',
                border: '0.1px solid rgba(255, 255, 255, 0.24)',
                borderTop: '1px solid rgba(255, 255, 255, 0.4)',
                borderLeft: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: ` // 内阴影 水平偏移 垂直偏移 模糊 扩散 颜色
                  inset 0 0.5px 1px 0 rgba(255, 255, 255, 0.97), 
                  inset 0.5px 0 2px 0 rgba(255, 255, 255, 0.8),
                  inset 0 -0.5px 1px 0 rgba(0, 0, 0, 0.3),
                  inset -0.5px 0 1px 0 rgba(0, 0, 0, 0.3),
                  0 8px 32px 0 rgba(0, 0, 0, 0.32)
                `
              }}

            >
              {/* Google Logo */}
              <div
                className="w-7 h-7 flex items-center justify-center rounded-full relative z-10"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6" style={{ background: 'none' }}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
              <span
                className="font-medium relative z-10"
                style={{ fontSize: 'clamp(0.75rem, 4vw, 0.8rem)' }}
              >
                {t('auth.loginWithGoogle')}
              </span>
            </button>

            {showAppleLogin && (
            <button
              onClick={handleAppleLogin}
              disabled={isLoading}
              className="w-10/12 mx-auto rounded-full px-6 py-4 text-white transition-all flex items-center justify-center gap-3 disabled:opacity-50 overflow-hidden relative"
              style={{
                background: 'linear-gradient(-3deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 50%)',
                backdropFilter: 'blur(90px) saturate(110%)',
                WebkitBackdropFilter: 'blur(90px) saturate(110%)',
                border: '0.1px solid rgba(255, 255, 255, 0.1)',

                boxShadow: `
                  inset 0 0.5px 1px 0 rgba(255, 255, 255, 0.8),
                  inset 0.5px 0 1px 0 rgba(255, 255, 255, 0.8),
                  inset 0 -0.5px 1px 0 rgba(0, 0, 0, 0.3),
                  inset -0.5px 0 1px 0 rgba(0, 0, 0, 0.3),
                  0 8px 32px 0 rgba(0, 0, 0, 0.1)
                `
              }}

            >
              {/* Apple Logo - Bitten Apple */}
              <div
                className="w-7 h-7 flex items-center justify-center rounded-full relative z-10"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ background: 'none' }}>
                  <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-5.18 3.014-2.2 3.817-.546 9.482 1.574 12.58 1.095 1.58 2.39 3.35 4.102 3.29 1.65-.07 2.27-1.07 4.26-1.07 1.98 0 2.54 1.07 4.27 1.04 1.75-.03 2.89-1.62 3.98-3.22 1.25-1.82 1.77-3.58 1.8-3.67-.04-.01-3.45-1.32-3.49-5.26-.03-3.33 2.72-4.93 2.85-5.02-1.58-2.33-4.05-2.59-5.02-2.63zM15.53 3.83c.84-1.02 1.41-2.44 1.25-3.86-1.21.05-2.67.81-3.54 1.84-.78.9-1.46 2.34-1.28 3.72 1.35.1 2.73-.68 3.57-1.7" />
                </svg>
              </div>
              <span
                className="font-medium relative z-10"
                style={{ fontSize: 'clamp(0.75rem, 4vw, 0.8rem)' }}
              >
                {t('auth.loginWithApple')}
              </span>
            </button>
            )}

            {/* Skip Button */}
            <button
              onClick={handleSkip}
              disabled={isLoading}
              className="w-full text-white/70 font-medium py-3 min-h-[44px] hover:text-white/90 active:text-white/90"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: 'clamp(0.95rem, 4vw, 1.05rem)'
              }}
            >
              {t('common.skip')}
            </button>
          </>
        ) : (
          /* Continue Button for logged in users */
          <button
            onClick={handleContinue}
            className="w-10/12 mx-auto rounded-full px-6 py-4 text-white transition-all flex items-center justify-center gap-3 overflow-hidden relative"
            style={{
              background: 'linear-gradient(-3deg,rgba(14, 72, 166, 0.18) 0%, rgba(11, 30, 84, 0.38) 100%)',
              backdropFilter: 'blur(90px) saturate(100%)',
              WebkitBackdropFilter: 'blur(90px) saturate(100%)',
              border: '0.1px solid rgba(255, 255, 255, 0.24)',
              borderTop: '1px solid rgba(255, 255, 255, 0.4)',
              borderLeft: '1px solid rgba(255, 255, 255, 0.4)',
              boxShadow: `
                inset 0 0.5px 1px 0 rgba(255, 255, 255, 0.97), 
                inset 0.5px 0 2px 0 rgba(255, 255, 255, 0.8),
                inset 0 -0.5px 1px 0 rgba(0, 0, 0, 0.3),
                inset -0.5px 0 1px 0 rgba(0, 0, 0, 0.3),
                0 8px 32px 0 rgba(0, 0, 0, 0.32)
              `
            }}
          >
            <span
              className="font-medium relative z-10"
              style={{ fontSize: 'clamp(0.875rem, 4vw, 1rem)' }}
            >
              {t('common.continue')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Splash;

